import { cn } from '@/lib/utils';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Separator } from '@/components/ui/separator';

const headingClassName = 'first:mt-0! mt-4! font-medium text-foreground';

const markdownComponents = {
    h1: ({ children }: { children?: React.ReactNode }) => (
        <h1 className={cn(headingClassName, 'text-lg')}>{children}</h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
        <h2 className={cn(headingClassName, 'text-base')}>{children}</h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
        <h3 className={cn(headingClassName, 'text-sm')}>{children}</h3>
    ),
    h4: ({ children }: { children?: React.ReactNode }) => (
        <h4 className={cn(headingClassName, 'font-medium text-sm')}>
            {children}
        </h4>
    ),
    h5: ({ children }: { children?: React.ReactNode }) => (
        <h5 className={cn(headingClassName, 'text-xs')}>{children}</h5>
    ),
    h6: ({ children }: { children?: React.ReactNode }) => (
        <h6
            className={cn(
                headingClassName,
                'font-medium text-muted-foreground text-xs'
            )}
        >
            {children}
        </h6>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
        <strong className="font-medium text-foreground">{children}</strong>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
        <ul className="flex flex-col gap-1 pl-4 list-disc">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
        <ol className="flex flex-col gap-1 pl-4 list-decimal">{children}</ol>
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="hover:text-foreground underline underline-offset-2"
        >
            {children}
        </a>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote className="pl-3 border-border border-l-2">
            {children}
        </blockquote>
    ),
    code: ({ children }: { children?: React.ReactNode }) => (
        <code className="bg-muted px-1 py-0.5 rounded-sm font-mono text-xs">
            {children}
        </code>
    ),
    pre: ({ children }: { children?: React.ReactNode }) => (
        <pre className="bg-muted p-2 rounded-md overflow-x-auto">
            {children}
        </pre>
    ),
    hr: () => <Separator />,
    table: ({ children }: { children?: React.ReactNode }) => (
        <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                {children}
            </table>
        </div>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
        <th className="px-2 py-1 border border-border font-medium text-foreground">
            {children}
        </th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
        <td className="px-2 py-1 border border-border">{children}</td>
    )
};

interface MarkdownContentProps {
    children: string;
    className?: string;
}

export function MarkdownContent({ children, className }: MarkdownContentProps) {
    return (
        <div className={cn('[&>*+*]:mt-2 text-sm', className)}>
            <Markdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
            >
                {children}
            </Markdown>
        </div>
    );
}
